import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { Mail, MessageSquare, Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { ContactEmails } from "components/common/ContactEmails";
import { GENERAL_INQUIRIES_EMAIL } from "lib/fortifyContact";
import { api, isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import { selectAuthUser, selectIsAuthenticated } from "store/reducer";
import swtAlert from "lib/sweetAlert";
import {
  isEmail,
  required,
  validateForm as runFormValidation,
  validateName,
  validatePhone,
  validateText,
} from "lib/validation";

const INITIAL_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

const formRowClass = "grid grid-cols-1 md:grid-cols-2 gap-4";

function buildContactPayload({ firstName, lastName, email, phone, subject, message }) {
  const payload = {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    email: email.trim(),
    subject: subject.trim(),
    message: message.trim(),
  };
  const phoneTrimmed = phone.trim();
  if (phoneTrimmed) payload.phone = phoneTrimmed;
  return payload;
}

export default function ContactPage() {
  const user = useSelector(selectAuthUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const emailLocked = isAuthenticated && Boolean(user?.email);
  const [formData, setFormData] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setFormData((prev) => ({ ...prev, email: user.email }));
    }
  }, [isAuthenticated, user?.email]);

  const validate = (values = formData) => {
    const newErrors = runFormValidation(values, {
      firstName: (v) => validateName(v, { label: "First name" }),
      lastName: (v) => validateName(v, { label: "Last name" }),
      email: [required("Email"), isEmail],
      phone: (v) => (v?.trim() ? validatePhone(v, { label: "Phone" }) : null),
      subject: (v) => validateText(v, { label: "Subject", min: 3, max: 120 }),
      message: (v) => validateText(v, { label: "Message", min: 10, max: 2000 }),
    });
    return newErrors;
  };

  const resetForm = () => {
    setFormData(
      emailLocked ? { ...INITIAL_VALUES, email: user.email } : INITIAL_VALUES
    );
    setTouched({});
    setErrors({});
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "email" && emailLocked) return;
    const nextValue = name === "phone" ? value.replace(/\D+/g, "").slice(0, 15) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validate();
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] || "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);
    setTouched(
      Object.keys(INITIAL_VALUES).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const resp = await api.post(
        API_ENDPOINTS.contact,
        buildContactPayload(formData),
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        await swtAlert.error(
          "Message not sent",
          resp?.message ||
            `Something went wrong. Please email us directly at ${GENERAL_INQUIRIES_EMAIL}.`
        );
        return;
      }

      await swtAlert.success(
        "Message sent",
        resp?.message ||
          "Thanks for reaching out — our team will get back to you shortly."
      );
      resetForm();
    } catch (err) {
      await swtAlert.error(
        "Message not sent",
        err?.message ||
          `Something went wrong. Please email us directly at ${GENERAL_INQUIRIES_EMAIL}.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const showError = (field) => Boolean(touched[field] && errors[field]);

  return (
    <>
      <Helmet>
        <title>Contact Us | Fortify</title>
        <meta
          name="description"
          content="Get in touch with Fortify Portal. Send us a message and our team will get back to you."
        />
      </Helmet>

      <section className="py-16 sm:py-20 bg-secondary/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Contact Us</h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto">
            Have a question about investing, raising capital, or the platform? Send us a
            message and our team will get back to you.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="grid gap-8 lg:grid-cols-3 lg:items-start">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <MessageSquare className="h-5 w-5 text-accent" aria-hidden />
                  Send us a message
                </CardTitle>
                <CardDescription>
                  Fields marked with <span className="text-destructive">*</span> are required.
                  Your message will be sent to{" "}
                  <a
                    href={`mailto:${GENERAL_INQUIRIES_EMAIL}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {GENERAL_INQUIRIES_EMAIL}
                  </a>
                  .
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form noValidate onSubmit={handleSubmit} className="space-y-6">
                  <div className={formRowClass}>
                    <div className="space-y-2">
                      <Label htmlFor="firstName">
                        First name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        autoComplete="given-name"
                        placeholder="Jane"
                        value={formData.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={showError("firstName")}
                        aria-describedby={
                          showError("firstName") ? "firstName-error" : undefined
                        }
                      />
                      {showError("firstName") ? (
                        <p id="firstName-error" className="text-xs text-destructive">
                          {errors.firstName}
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">
                        Last name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        autoComplete="family-name"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={showError("lastName")}
                        aria-describedby={
                          showError("lastName") ? "lastName-error" : undefined
                        }
                      />
                      {showError("lastName") ? (
                        <p id="lastName-error" className="text-xs text-destructive">
                          {errors.lastName}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className={formRowClass}>
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        readOnly={emailLocked}
                        disabled={emailLocked}
                        className={emailLocked ? "bg-muted/50" : undefined}
                        aria-invalid={showError("email")}
                        aria-describedby={showError("email") ? "email-error" : undefined}
                      />
                      {showError("email") ? (
                        <p id="email-error" className="text-xs text-destructive">
                          {errors.email}
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        Phone <span className="text-muted-foreground">(optional)</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        pattern="[0-9]*"
                        maxLength={15}
                        placeholder="5551234567"
                        value={formData.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={showError("phone")}
                        aria-describedby={showError("phone") ? "phone-error" : undefined}
                      />
                      {showError("phone") ? (
                        <p id="phone-error" className="text-xs text-destructive">
                          {errors.phone}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">
                      Subject <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      placeholder="How can we help?"
                      value={formData.subject}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={showError("subject")}
                      aria-describedby={
                        showError("subject") ? "subject-error" : undefined
                      }
                    />
                    {showError("subject") ? (
                      <p id="subject-error" className="text-xs text-destructive">
                        {errors.subject}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">
                      Message <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={6}
                      placeholder="Share the details of your question or request..."
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      aria-invalid={showError("message")}
                      aria-describedby={
                        showError("message") ? "message-error" : "message-counter"
                      }
                    />
                    <div className="flex items-start justify-between gap-3 text-xs">
                      {showError("message") ? (
                        <p id="message-error" className="text-destructive">
                          {errors.message}
                        </p>
                      ) : (
                        <span id="message-counter" className="text-muted-foreground">
                          {formData.message.trim().length}/2000 characters
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-muted-foreground">
                      Submitting sends your message to our team.
                    </p>
                    <Button type="submit" size="lg" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" aria-hidden />
                          Send message
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Other ways to reach us</CardTitle>
                <CardDescription>
                  Use the right address for the fastest response.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ContactEmails className="space-y-3 text-sm" />
                <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
                  Fortify Portal, Inc. is not registered as a dealer under Canadian securities
                  legislation and does not provide investment advice.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
}
